import { ApolloError, useMutation, useQuery } from '@apollo/client'
import { useContext, useEffect, useMemo, useState } from 'react'

import { NotificationContext } from '@/contexts/NotificationContext'
import {
  UPDATE_TENANT_TABLE_COLUMN_VISIBILITY,
  UPDATE_USER_TABLE_COLUMN_VISIBILITY,
  USER_TABLE_COLUMN_SETTINGS,
} from '@/graphql'

import {
  buildNextTenantHiddenColumns,
  buildNextUserHiddenColumns,
  EMPTY_TABLE_COLUMN_VISIBILITY_SETTINGS,
  getVisibleColumnIds,
  normalizeTableColumnVisibilitySettings,
  TableColumnVisibilitySettings,
} from './helpers'

const getErrorMessage = (error: unknown) => {
  if (error instanceof ApolloError) {
    return error.message
  }

  if (error instanceof Error) {
    return error.message
  }

  return ''
}

const isAuthorizationError = (error: unknown) => {
  const message = getErrorMessage(error).toLowerCase()

  return (
    message.includes('permission') ||
    message.includes('not authorized') ||
    message.includes('not authorised') ||
    message.includes('forbidden') ||
    message.includes('unauthorized')
  )
}

type TableColumnVisibilitySettingsResponse = {
  tableId: string
  tenantHiddenColumns: string[]
  userHiddenColumns: string[]
  effectiveHiddenColumns: string[]
}

type UserTableColumnSettingsQuery = {
  userTableColumnSettings: TableColumnVisibilitySettingsResponse
}

type UserTableColumnSettingsQueryVariables = {
  tableId: string
}

type UpdateUserTableColumnVisibilityMutation = {
  updateUserTableColumnVisibility: TableColumnVisibilitySettingsResponse
}

type UpdateUserTableColumnVisibilityMutationVariables = {
  tableId: string
  hiddenColumns: string[]
}

type UpdateTenantTableColumnVisibilityMutation = {
  updateTenantTableColumnVisibility: TableColumnVisibilitySettingsResponse
}

type UpdateTenantTableColumnVisibilityMutationVariables = {
  tableId: string
  hiddenColumns: string[]
}

export const useTableColumnVisibility = <TColumnId extends string>(
  tableId: string,
  supportedColumns: readonly TColumnId[],
  options?: {
    updateErrorMessage?: string
    permissionErrorMessage?: string
  },
) => {
  const { setError } = useContext(NotificationContext)
  const [resolvedSettings, setResolvedSettings] =
    useState<TableColumnVisibilitySettings>({
      ...EMPTY_TABLE_COLUMN_VISIBILITY_SETTINGS,
      tableId,
    })

  const { data, loading, error } = useQuery<
    UserTableColumnSettingsQuery,
    UserTableColumnSettingsQueryVariables
  >(USER_TABLE_COLUMN_SETTINGS, {
    variables: { tableId },
    fetchPolicy: 'cache-and-network',
  })

  const [updateUserTableColumnVisibility, { loading: userMutationLoading }] =
    useMutation<
      UpdateUserTableColumnVisibilityMutation,
      UpdateUserTableColumnVisibilityMutationVariables
    >(UPDATE_USER_TABLE_COLUMN_VISIBILITY)

  const [
    updateTenantTableColumnVisibility,
    { loading: tenantMutationLoading },
  ] = useMutation<
    UpdateTenantTableColumnVisibilityMutation,
    UpdateTenantTableColumnVisibilityMutationVariables
  >(UPDATE_TENANT_TABLE_COLUMN_VISIBILITY)

  useEffect(() => {
    if (!data?.userTableColumnSettings) {
      return
    }

    setResolvedSettings(
      normalizeTableColumnVisibilitySettings(
        data.userTableColumnSettings,
        supportedColumns,
      ),
    )
  }, [data?.userTableColumnSettings, supportedColumns])

  const settings = useMemo(
    () =>
      normalizeTableColumnVisibilitySettings(
        resolvedSettings,
        supportedColumns,
      ),
    [resolvedSettings, supportedColumns],
  )

  const applyUserVisibility = async (columnId: string, hidden: boolean) => {
    try {
      const response = await updateUserTableColumnVisibility({
        variables: {
          tableId,
          hiddenColumns: buildNextUserHiddenColumns(settings, columnId, hidden),
        },
      })

      if (response.data?.updateUserTableColumnVisibility) {
        setResolvedSettings(
          normalizeTableColumnVisibilitySettings(
            response.data.updateUserTableColumnVisibility,
            supportedColumns,
          ),
        )
      }
    } catch (mutationError) {
      setError(
        options?.updateErrorMessage || 'Unable to update column visibility.',
      )
    }
  }

  const applyTenantVisibility = async (columnId: string, hidden: boolean) => {
    try {
      const response = await updateTenantTableColumnVisibility({
        variables: {
          tableId,
          hiddenColumns: buildNextTenantHiddenColumns(
            settings,
            columnId,
            hidden,
          ),
        },
      })

      if (response.data?.updateTenantTableColumnVisibility) {
        setResolvedSettings(
          normalizeTableColumnVisibilitySettings(
            response.data.updateTenantTableColumnVisibility,
            supportedColumns,
          ),
        )
      }
    } catch (mutationError) {
      setError(
        isAuthorizationError(mutationError)
          ? options?.permissionErrorMessage ||
              'You are not authorized to manage columns for everyone.'
          : options?.updateErrorMessage ||
              'Unable to update column visibility.',
      )
    }
  }

  const effectiveHiddenSet = useMemo(
    () => new Set(settings.effectiveHiddenColumns),
    [settings.effectiveHiddenColumns],
  )
  const userHiddenSet = useMemo(
    () => new Set(settings.userHiddenColumns),
    [settings.userHiddenColumns],
  )
  const tenantHiddenSet = useMemo(
    () => new Set(settings.tenantHiddenColumns),
    [settings.tenantHiddenColumns],
  )

  return {
    settings,
    loading,
    error,
    hideForUser: (columnId: string) => applyUserVisibility(columnId, true),
    showForUser: (columnId: string) => applyUserVisibility(columnId, false),
    hideForTenant: (columnId: string) => applyTenantVisibility(columnId, true),
    showForTenant: (columnId: string) => applyTenantVisibility(columnId, false),
    effectiveHiddenSet,
    userHiddenSet,
    tenantHiddenSet,
    visibleColumnIds: getVisibleColumnIds(supportedColumns, settings),
    mutationLoading: userMutationLoading || tenantMutationLoading,
  }
}
